import {
  CompanyRoleDTO,
  ModulePermissionOverridesDTO,
  PermissionCatalogueModuleDTO,
  PermissionDTO,
  PermissionGroupDTO,
} from '@vyce/core/src/api/types';

import { ModulePermissionState, VisibleStaffModule } from '../types';

export type PermissionIndex = Map<string, PermissionDTO>;

export interface CatalogueIndex {
  /** module_system_name -> permissions, flattened in group order. */
  byModule: Map<string, PermissionDTO[]>;
  /** module_system_name -> the groups themselves, for the section headings. */
  groupsByModule: Map<string, PermissionGroupDTO[]>;
  /** system_name -> permission. */
  index: PermissionIndex;
  /** system_name -> the permissions that imply it. */
  impliedBy: Map<string, string[]>;
}

export const buildCatalogueIndex = (modules: PermissionCatalogueModuleDTO[]): CatalogueIndex => {
  const byModule = new Map<string, PermissionDTO[]>();
  const groupsByModule = new Map<string, PermissionGroupDTO[]>();
  const index: PermissionIndex = new Map();
  const impliedBy = new Map<string, string[]>();

  modules.forEach(module => {
    const groups = module.groups || [];
    const flat = groups.reduce<PermissionDTO[]>((acc, group) => acc.concat(group.permissions || []), []);
    groupsByModule.set(module.module_system_name, groups);
    byModule.set(module.module_system_name, flat);
    flat.forEach(permission => {
      index.set(permission.system_name, permission);
      (permission.implied_permissions || []).forEach(implied => {
        const owners = impliedBy.get(implied) || [];
        owners.push(permission.system_name);
        impliedBy.set(implied, owners);
      });
    });
  });

  return { byModule, groupsByModule, index, impliedBy };
};

// Transitive union of implied_permissions. The catalogue is server data, so guard
// against cycles rather than trusting it not to have any.
export const closure = (explicit: Iterable<string>, index: PermissionIndex): Set<string> => {
  const result = new Set<string>();
  const queue = Array.from(explicit);
  while (queue.length) {
    const current = queue.pop() as string;
    if (result.has(current)) continue;
    result.add(current);
    (index.get(current)?.implied_permissions || []).forEach(implied => {
      if (!result.has(implied)) queue.push(implied);
    });
  }
  return result;
};

export const setsEqual = (a: Set<string>, b: Iterable<string>) => {
  const other = b instanceof Set ? b : new Set(b);
  if (a.size !== other.size) return false;
  let equal = true;
  a.forEach(item => {
    if (!other.has(item)) equal = false;
  });
  return equal;
};

// The server sends the effective set; we need the subset the user actually ticked,
// so that unticking a parent releases everything it dragged in. A permission is
// dropped only if the rest still implies it, and we verify the closure afterwards
// so mutual implications can't silently drop both sides.
export const explicitFromEffective = (effective: string[], index: PermissionIndex): Set<string> => {
  const all = new Set(effective);
  const explicit = new Set(effective);

  effective.forEach(permission => {
    const rest = new Set(explicit);
    rest.delete(permission);
    if (closure(rest, index).has(permission)) {
      explicit.delete(permission);
    }
  });

  const covered = closure(explicit, index);
  all.forEach(permission => {
    if (!covered.has(permission)) explicit.add(permission);
  });

  return explicit;
};

/** Ticked because something else needs it — shown as checked and disabled. */
export const lockedSet = (explicit: Set<string>, effectiveDraft: Set<string>): Set<string> => {
  const locked = new Set<string>();
  effectiveDraft.forEach(permission => {
    if (!explicit.has(permission)) locked.add(permission);
  });
  return locked;
};

/** Which ticked permissions are the reason a locked row can't be unticked. */
export const lockReasons = (
  permission: string,
  explicit: Set<string>,
  catalogue: CatalogueIndex
): string[] => {
  const reasons: string[] = [];
  explicit.forEach(candidate => {
    if (closure([candidate], catalogue.index).has(permission) && candidate !== permission) {
      reasons.push(catalogue.index.get(candidate)?.display_name || candidate);
    }
  });
  return Array.from(new Set(reasons));
};

export const toDisplayName = (systemName: string, index: PermissionIndex) =>
  index.get(systemName)?.display_name || systemName;

// roles/available has carried the role's grant under more than one field name and
// in more than one shape, so read whichever is present instead of trusting one.
const ROLE_PERMISSION_FIELDS = [
  'permission_names',
  'permissions',
  'default_permissions',
  'role_defaults',
  'permission_system_names',
];

const rawRolePermissions = (role: CompanyRoleDTO): unknown[] | undefined => {
  const source = role as unknown as Record<string, unknown>;
  for (const field of ROLE_PERMISSION_FIELDS) {
    const value = source[field];
    if (Array.isArray(value)) return value;
  }
  return undefined;
};

const entryToName = (entry: unknown): string | undefined => {
  if (typeof entry === 'string') return entry;
  if (entry && typeof entry === 'object') {
    const record = entry as Record<string, unknown>;
    const candidate = record.system_name ?? record.name ?? record.display_name;
    if (typeof candidate === 'string') return candidate;
  }
  return undefined;
};

/** What the owner role carries instead of a list — it holds everything. */
const FULL_ACCESS = 'full access';

/**
 * Which of this module's permissions a role grants, as catalogue system_names.
 *
 * roles/available describes a role by **display name** ("Edit locations"), so
 * entries are matched against the module's catalogue labels; a system_name is
 * accepted too, in case the field ever changes shape. Names belonging to other
 * modules — a Time role also lists "Invite workers", which lives under Company —
 * are dropped: each module's panel shows and saves only its own permissions.
 *
 * `null` means the role carries no usable information, and the caller must then
 * leave the checkboxes alone. An empty list counts as no information too: a role
 * that grants nothing has no reason to exist, so an empty array is the payload
 * omitting the data rather than describing a real grant.
 */
export const roleDefaultPermissions = (
  role: CompanyRoleDTO,
  modulePermissions: PermissionDTO[],
  index: PermissionIndex
): string[] | null => {
  const raw = rawRolePermissions(role);
  if (!raw?.length) return null;

  const names = raw.map(entryToName).filter(Boolean) as string[];
  // The owner role is spelled 'Full access' rather than enumerated.
  if (names.some(name => name.trim().toLowerCase() === FULL_ACCESS)) {
    return modulePermissions.map(permission => permission.system_name);
  }

  const byDisplayName = new Map(
    modulePermissions.map(permission => [
      permission.display_name.trim().toLowerCase(),
      permission.system_name,
    ])
  );

  const resolved = names.reduce<string[]>((acc, name) => {
    if (index.has(name)) {
      acc.push(name);
      return acc;
    }
    const bySystemName = byDisplayName.get(name.trim().toLowerCase());
    if (bySystemName) acc.push(bySystemName);
    return acc;
  }, []);

  // Nothing matched at all: either the format is not one we understand, or this
  // role only grants permissions that live in other modules. Either way, guessing
  // an empty grant would silently strip the module's permissions.
  return resolved.length ? resolved : null;
};

// A closure keeps whatever it reaches, including other modules' permissions; the
// server reports each module's sets in that module's own names only.
export const ownPermissions = (module: VisibleStaffModule | undefined, names: Iterable<string>) => {
  const own = new Set((module?.permissions || []).map(permission => permission.system_name));
  return new Set(Array.from(names).filter(name => own.has(name)));
};

export const emptyPermissionState = (): ModulePermissionState => ({
  roleDefaults: [],
  added: [],
  removed: [],
  baselineEffective: [],
  explicit: [],
  clearResourceOverrides: true,
  conflicts: [],
  hasOverridesRow: false,
});

export const permissionStateFromDTO = (
  dto: ModulePermissionOverridesDTO,
  catalogue: CatalogueIndex
): ModulePermissionState => ({
  roleDefaults: dto.role_defaults || [],
  added: dto.added || [],
  removed: dto.removed || [],
  baselineEffective: dto.effective || [],
  explicit: Array.from(explicitFromEffective(dto.effective || [], catalogue.index)),
  clearResourceOverrides: true,
  conflicts: [],
  hasOverridesRow: true,
});

/**
 * The module's ticks a freshly picked role starts from: its grant closed over
 * implied_permissions, like the role_defaults the overrides endpoint returns, and
 * kept to this module's own names. Without the closure the panel would read the
 * implied extras as "changed from default" the moment the role is picked.
 * `null` when the role payload doesn't say what it grants — see roleDefaultPermissions.
 */
export const moduleRoleDefaults = (
  role: CompanyRoleDTO,
  module: VisibleStaffModule | undefined,
  catalogue: CatalogueIndex
): string[] | null => {
  const granted = roleDefaultPermissions(role, module?.permissions || [], catalogue.index);
  if (granted === null) return null;
  return Array.from(ownPermissions(module, closure(granted, catalogue.index)));
};
